import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContentProps } from 'react-toastify';
import { Button } from '@app/components';
import { Form } from 'react-bootstrap';
import { ErrorMessage, FieldArray, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { getAllActiveFranchise, getSubjectsByFranchise } from '@app/services/franchise';
import Select from "react-select";
import { createLead, getAllFoundUsVia, getAllGotInTouch, getAllLeadStatus } from '@app/services/lead';
import { getCentresByFranchiseId } from '@app/services/centre';

const AddLead = () => {

    const [isLoading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [franchises, setFranchises] = useState([]);
	const [isLoadingFranchise, setLoadingFranchise] = useState(false);
	const [subjects, setSubjects] = useState([]);
	const [isSubjectsLoading, setSubjectsLoading] = useState(false);
	const [gotInTouchOptions, setGotInTouchOptions] = useState([]);
	const [foundUsViaOptions, setFoundUsViaOptions] = useState([]);
    const [centres, setCentres] = useState([]);
    const [hasCentres, setHasCentres] = useState(true);
	const [leadStauses, setLeadStauses] = useState([]);
    const emptyStudent = {first_name: '', last_name:'', age: '', subjects: [], assessment_location: '', lead_inquiry_status_id: '', comment: ''}

    useEffect(() => {
        getAllActiveFranchiseData()
        getAllGotInTouchAPI()
        getAllFoundUsViaAPI()
        getAllLeadStatusAPI()

        return () => {
            // Anything in here is fired on component unmount.
           
        }
    },[]);

    const getAllActiveFranchiseData = async () => {
        try {
            setLoadingFranchise(true);
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) {
                setLoadingFranchise(false);
                setFranchises(resp?.collections);
            } else {
                setLoadingFranchise(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoadingFranchise(false);
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const getFranchiseSubjects = async (franchise_id) => {
        try {
            setSubjectsLoading(true)
            const resp = await getSubjectsByFranchise(franchise_id)
            if (resp.success === true) {
                setSubjectsLoading(false)
                setSubjects(resp?.collections.filter((sub) => sub.status == 'active'))
            } else {
                setSubjectsLoading(false)
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setSubjectsLoading(false)
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getAllGotInTouchAPI = async () => {
        try {
            const resp = await getAllGotInTouch()
            if (resp.success === true) {
                setGotInTouchOptions(resp?.collections)
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getAllFoundUsViaAPI = async () => {
        try {
            const resp = await getAllFoundUsVia()
            if (resp.success === true) {
                setFoundUsViaOptions(resp?.collections)
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getAllLeadStatusAPI = async () => {
        try {
            const resp = await getAllLeadStatus()
            if (resp.success === true) {
                setLeadStauses(resp?.collections)
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getAllActiveCentresByFranchise = async (id) => {
        try{
            const resp = await getCentresByFranchiseId(id);
            if (resp.success === true) {
                setHasCentres(resp.collections?.length > 0);
                const centres = resp.collections?.length > 0
                        ? [{ id: "", name: "Please Select Centre" }, ...resp.collections]
                        : [{ id: "", name: "No Centres" }];
                setCentres(centres);
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        }catch (error: any){
            toast.error(error?.message || 'Internal server error');
        }
    }

    const createLeadAPI = async (payload) => {
        try {
            setLoading(true);
            const resp = await createLead(payload);
            if (resp.success === true) {
                if(resp.collections.length > 0){
                    toast(CustomNotification, {
                        data: {
                            title: 'The following actions have been taken based on your lead data submission:',
                            content: resp?.collections,
                        },
                        autoClose: false,
                    });
                }else{
                    toast.success(resp?.message);
                }
                setLoading(false);
                navigate(-1);
            } else {
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const formik = useFormik({
        initialValues: {
            franchise_id: '',
            centre_id: '',
            date_of_inquiry: '',
            got_in_touch_id: '',
            got_in_touch_other_text: '',
            found_us_id: '',
            found_via_other_text: '',
            parent_first_name: '',
            parent_last_name: '',
            parent_phone: '',
            parent_email: '',
            students: [emptyStudent]
        },
        validationSchema: Yup.object({
            franchise_id: Yup.string().required('Please select franchise'),
            date_of_inquiry: Yup.date().required('Please enter date of enquiry'),
            got_in_touch_id: Yup.string().required('Please select one option'),
            got_in_touch_other_text: Yup.string().trim().when("got_in_touch_id", {
                is: (val: string) => (val === '7'),
                then: Yup.string().required('Please enter how they got in touch'),
            }),
            found_us_id: Yup.string().required('Please select one option'),
            found_via_other_text: Yup.string().trim().when("found_us_id", {
                is: (val: string) => (val == '8' || val == '9'),
                then: Yup.string().required('Please where did you find about us'),
            }),
            parent_first_name: Yup.string().trim().required('Please enter parent first name'),
            // parent_last_name: Yup.string().trim().required('Please enter parent last name'),
            parent_phone: Yup.string().matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/, "Please enter valid number"),
            parent_email: Yup.string().email('Please enter valid email'),
            students: Yup.array().of(Yup.object({
                first_name: Yup.string().trim().required('Please enter student first name'),
                // last_name: Yup.string().trim().required('Please enter student last name'),
                // age: Yup.number().required('Please enter student age'),
                assessment_location: Yup.string().required('Please enter assessment location'),
                lead_inquiry_status_id: Yup.string().required('Please select status'),
                comment: Yup.string().required('Please enter a comment'),
                subjects: Yup.array()
                .of(Yup.string().trim())
                .min(1, "Please select at least 1 subject")
                .required("Please select subject"),
            }))
        }),
        onSubmit: (values) => {
            let flag = false
            values.students.map((obj) => {
                if(obj.lead_inquiry_status_id == '7' || obj.lead_inquiry_status_id == '8'){
                    flag = true        
                }
            })

            if(flag){
                if(values.parent_email == ''){
                    toast.error('Please enter parent email')
                }else if(values.parent_phone == ''){
                    toast.error('Please enter parent phone')
                }else if(!(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/).test(values.parent_phone)){
                    toast.error('Please enter valid number')
                }else{
                    createLeadAPI(values)
                }
            }else{
                createLeadAPI(values)
            }
        },
        enableReinitialize: true,
    });

    const handleSubjectChange = (options: readonly typeof Option[], setFieldValue, index) => {
        setFieldValue(
            `students.${index}.subjects`,
            [].slice.call(options).map((option) => option.subject_id)
        );
    };

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <FormikProvider value={formik}>
                <form className='form-horizontal' onSubmit={formik.handleSubmit}>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="col-md-12 text-center">
                                        <span style={{fontSize: '1.5rem', fontWeight: 'bold'}}>Add New Lead</span>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="external-event" style={{backgroundColor: "rgb(248 213 76)", position: 'relative', zIndex: 'auto', left: '0px', top: '0px', width: '13%', fontWeight: 400, cursor:'default'}}>Primary Details</div>
                                        <div className="card">
                                            <div className="card-body" style={{backgroundColor: 'rgb(248 241 208)'}}>
                                                <div className="row">
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">Franchise <span className="required">*</span></label>
                                                            <Select
                                                                // ref={selectRef}
                                                                name="franchiseId"
                                                                placeholder="Search By Franchise"
                                                                options={franchises}
                                                                getOptionValue={(option)=>`${option['id']}`}
                                                                getOptionLabel={(option)=> option['name'] }
                                                                onChange={ (option) => { 
                                                                    formik.handleChange
                                                                    if(option){
                                                                        formik.setFieldValue("franchise_id", option.id)
                                                                        getFranchiseSubjects(option.id)
                                                                        getAllActiveCentresByFranchise(option.id)
                                                                    }else{
                                                                        formik.setFieldValue("franchise_id", '')
                                                                        setSubjects([])
                                                                        setCentres([])
                                                                    }
                                                                }}
                                                                // value={formik.values.franchise_id}
                                                                isLoading={isLoadingFranchise}
                                                                isClearable={true}
                                                            />
                                                            {(formik.touched.franchise_id && formik.errors.franchise_id) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.franchise_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="exampleInputGrade" className="">Centre</label>
                                                            <select
                                                                className="form-control"
                                                                name="centre_id"
                                                                onChange={ (e) => { 
                                                                    formik.setFieldValue("centre_id", e.target.value)
                                                                }}
                                                                value={formik.values.centre_id}
                                                                disabled={!hasCentres}
                                                            >
                                                                {centres.map((obj) => (
                                                                    <option key={obj.id} value={obj.id}>
                                                                        {obj.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {(formik.touched.centre_id && formik.errors.centre_id) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.centre_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">Date Of Enquiry <span className="required">*</span></label>
                                                                <Form.Control 
                                                                    type="date" 
                                                                    name="date_of_inquiry" 
                                                                    id="date_of_inquiry" 
                                                                    placeholder="Enter date of enquiry" 
                                                                    value={formik.values.date_of_inquiry}
                                                                    onChange={formik.handleChange}
                                                                />
                                                                 {(formik.touched.date_of_inquiry && formik.errors.date_of_inquiry) && (
                                                                    <div className="text-danger">
                                                                        {formik.errors.date_of_inquiry}
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">How They Got In Touch ? <span className="required">*</span></label>
                                                            <select
                                                                className="form-control"
                                                                name="got_in_touch_id"
                                                                // onChange={ (e) => {
                                                                //     setFieldValue('got_in_touch_id', e.target.value)
                                                                // }}
                                                                onChange={formik.handleChange}
                                                                value={formik.values.got_in_touch_id}
                                                            >
                                                                <option value="">Please Select</option>
                                                                {gotInTouchOptions.map((obj) => (
                                                                    <option key={obj.id} value={obj.id}>
                                                                        {obj.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {(formik.touched.got_in_touch_id && formik.errors.got_in_touch_id) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.got_in_touch_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    { formik.values.got_in_touch_id == '7' && (
                                                        <div className="col-sm-3">
                                                            <div className="form-group">
                                                                <label htmlFor="got_in_touch_other_text"> Other <span className="required">*</span></label>
                                                                <Form.Control 
                                                                    type="text" 
                                                                    name="got_in_touch_other_text" 
                                                                    id="got_in_touch_other_text" 
                                                                    placeholder="Enter How They Got In Touch" 
                                                                    value={formik.values.got_in_touch_other_text}
                                                                    onChange={formik.handleChange}
                                                                />
                                                                {(formik.touched.got_in_touch_other_text && formik.errors.got_in_touch_other_text) && (
                                                                    <div className="text-danger">
                                                                        {formik.errors.got_in_touch_other_text}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">Where Did You Find About Us ? <span className="required">*</span></label>
                                                            <select
                                                                className="form-control"
                                                                name="found_us_id"
                                                                // onChange={ (e) => {
                                                                //     setFieldValue('found_us_id', e.target.value)
                                                                // }}
                                                                onChange={formik.handleChange}
                                                                value={formik.values.found_us_id}
                                                            >
                                                                <option value="">Please Select</option>
                                                                {foundUsViaOptions.map((obj) => (
                                                                    <option key={obj.id} value={obj.id}>
                                                                        {obj.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {(formik.touched.found_us_id && formik.errors.found_us_id) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.found_us_id}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    { (formik.values.found_us_id == '8' || formik.values.found_us_id == '9') && (
                                                        <div className="col-sm-3">
                                                            <div className="form-group">
                                                                <label htmlFor="found_via_other_text"> { formik.values.found_us_id == '9' ? 'Other' : 'Referral'} <span className="required">*</span></label>
                                                                <Form.Control 
                                                                    type="text" 
                                                                    name="found_via_other_text" 
                                                                    id="found_via_other_text" 
                                                                    placeholder="Enter Where Did You Find About Us" 
                                                                    value={formik.values.found_via_other_text}
                                                                    onChange={formik.handleChange}
                                                                />
                                                                {(formik.touched.found_via_other_text && formik.errors.found_via_other_text) && (
                                                                    <div className="text-danger">
                                                                        {formik.errors.found_via_other_text}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="external-event" style={{backgroundColor: "rgb(184 206 65)", position: 'relative', zIndex: 'auto', left: '0px', top: '0px', width: '13%', fontWeight: 400, cursor:'default'}}>Parent Details</div>
                                        <div className="card">
                                            <div className="card-body" style={{backgroundColor: 'rgb(235 240 204)'}}>
                                                <div className="row">
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">First Name <span className="required">*</span></label>
                                                            <Form.Control 
                                                                type="text" 
                                                                name="parent_first_name" 
                                                                id="parent_first_name" 
                                                                placeholder="Enter First Name" 
                                                                value={formik.values.parent_first_name}
                                                                onChange={formik.handleChange}
                                                            />
                                                            {(formik.touched.parent_first_name && formik.errors.parent_first_name) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.parent_first_name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">Last Name </label>
                                                            <Form.Control 
                                                                type="text" 
                                                                name="parent_last_name" 
                                                                id="parent_last_name" 
                                                                placeholder="Enter Last Name" 
                                                                value={formik.values.parent_last_name}
                                                                onChange={formik.handleChange}
                                                            />
                                                            {(formik.touched.parent_last_name && formik.errors.parent_last_name) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.parent_last_name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">Contact (WhatsApp Number) </label>
                                                            <Form.Control 
                                                                type="number" 
                                                                name="parent_phone" 
                                                                id="parent_phone" 
                                                                placeholder="Enter Phone" 
                                                                value={formik.values.parent_phone}
                                                                onChange={formik.handleChange}
                                                            />
                                                            {(formik.touched.parent_phone && formik.errors.parent_phone) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.parent_phone}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-3">
                                                        <div className="form-group">
                                                            <label htmlFor="inputModule1">Email </label>
                                                            <Form.Control 
                                                                type="email" 
                                                                name="parent_email" 
                                                                id="parent_email" 
                                                                placeholder="Enter Email" 
                                                                value={formik.values.parent_email}
                                                                onChange={formik.handleChange}
                                                            />
                                                            {(formik.touched.parent_email && formik.errors.parent_email) && (
                                                                <div className="text-danger">
                                                                    {formik.errors.parent_email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="external-event" style={{backgroundColor: "rgb(126 203 65)", position: 'relative', zIndex: 'auto', left: '0px', top: '0px', width: '13%', fontWeight: 400, cursor:'default'}}>Student Details</div>
                                        <FieldArray name="students">
                                            {({ push, remove}) => (
                                                <div className="card" key='students'>
                                                    <div className="card-body" style={{backgroundColor: 'rgb(222 239 207)'}}>
                                                        {formik.values.students.map((student, index) => (
                                                            <div key={index}>
                                                            <div className="row" key={index}>
                                                                <div className="col-sm-3">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">First Name <span className="required">*</span></label>
                                                                        <Form.Control 
                                                                            type="text" 
                                                                            name={`students.${index}.first_name`}
                                                                            // id="first_name" 
                                                                            placeholder="Enter First Name" 
                                                                            // value={formik.values.first_name}
                                                                            onChange={formik.handleChange}
                                                                        />
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.first_name`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-3">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">Last Name </label>
                                                                        <Form.Control 
                                                                            type="text" 
                                                                            name={`students.${index}.last_name`}
                                                                            // id="last_name" 
                                                                            placeholder="Enter Last Name" 
                                                                            // value={formik.values.last_name}
                                                                            onChange={formik.handleChange}
                                                                        />
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.last_name`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-3">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">Age </label>
                                                                        <Form.Control 
                                                                            type="number" 
                                                                            name={`students.${index}.age`}
                                                                            // id="age" 
                                                                            placeholder="Enter Age" 
                                                                            // value={formik.values.age}
                                                                            onChange={formik.handleChange}
                                                                        />
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.age`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-3">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">Subject(s) <span className="required">*</span></label>
                                                                        <Select
                                                                            placeholder={<div>-- Please select --</div>}
                                                                            isMulti
                                                                            options={subjects}
                                                                            closeMenuOnSelect={false}
                                                                            getOptionLabel={(option)=>option.name}
                                                                            getOptionValue={(option)=>option.subject_id}
                                                                            name={`students.${index}.subjects`}
                                                                            value={
                                                                                subjects?.length
                                                                                ? subjects.filter((obj) =>
                                                                                    formik.values.students[index].subjects.includes(obj.subject_id)
                                                                                    )
                                                                                : formik.values.students[index].subjects
                                                                            } // set selected values
                                                                            onChange={ (e) => {handleSubjectChange(e, formik.setFieldValue, index)}}
                                                                            isLoading={isSubjectsLoading}
                                                                        />
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.subjects`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-3">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">Assessment Location <span className="required">*</span></label>
                                                                        <Form.Control 
                                                                            type="text" 
                                                                            name={`students.${index}.assessment_location`} 
                                                                            // id="assessment_location" 
                                                                            placeholder="Enter Assessment Location" 
                                                                            // value={values.assessment_location}
                                                                            onChange={formik.handleChange}
                                                                        />
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.assessment_location`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-3">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">Status <span className="required">*</span></label>
                                                                        <select
                                                                            className="form-control"
                                                                            name={`students.${index}.lead_inquiry_status_id`}
                                                                            onChange={formik.handleChange}
                                                                            // value={values.lead_inquiry_status_id}
                                                                        >
                                                                            <option value="">Please Select</option>
                                                                            {leadStauses.map((obj) => (
                                                                                <option key={obj.id} value={obj.id}>
                                                                                    {obj.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.lead_inquiry_status_id`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-4">
                                                                    <div className="form-group">
                                                                        <label htmlFor="inputModule1">Comment <span className="required">*</span></label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            name={`students.${index}.comment`}
                                                                            onChange={formik.handleChange}
                                                                            // value={formik.values.comment}
                                                                        ></textarea>
                                                                        <div className="text-danger">
                                                                            <ErrorMessage name={`students.${index}.comment`} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                { formik.values.students.length > 1 && (
                                                                    <div className="col-sm-2 d-flex align-items-center form_wrapper">
                                                                        <i className="fas fa-times-circle" onClick={() => remove(index)} style={{cursor:'pointer'}}></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <hr/>
                                                            </div>
                                                        ))}
                                                        <i className="cursor-pointer fas fa-plus-circle" onClick={() => push(emptyStudent)} style={{cursor:'pointer'}}> Add More Student</i>
                                                    </div>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <div className="col-md-12">
                                        <Button type="submit" isLoading={isLoading} className="btn btn-warning mr-2">Save Lead </Button>
                                        <Button className="btn btn-default" onClick={() => navigate(-1)}>Back</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                </FormikProvider>
            </div>
        </section>
    </div>
  )
}

export default AddLead;

type CustomNotificationProps = ToastContentProps<{
    title: string;
    content: any;
}>;

function CustomNotification({
    data,
}: CustomNotificationProps) {

    return (
        <div className="flex flex-col w-full">
            <h3 className='text-sm text-zinc-800'>
                <b>{data.title}</b>
            </h3>

            <div className="flex items-center justify-between">
                <p className="text-sm">
                    {data.content && data.content.map((obj, i, arr) => (
                        <div key={i}>
                            <div>Student Name: {obj.student_name} </div>
                            {obj.exist && (
                                <div className='text-danger'>Already exist: {obj.exist ?? '---'}</div>
                            )}
                            {obj.created && (
                                <div className='text-success'>Created: {obj.created ?? '---'}</div>
                            )}
                            {i != arr.length - 1 && <br />}
                        </div>
                    ))}
                </p>
            </div>
        </div>
    );
}